import { doc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Unified likes management utility for consistent likes handling across all components
 */
export class LikesManager {
  
  /**
   * Get the consistent likes count for display
   * @param {Object} wishlist - The wishlist object
   * @returns {number} - The likes count (never NaN)
   */
  static getLikesCount(wishlist) {
    if (!wishlist) return 0;
    
    // Prioritize likesCount if it exists and is a valid number
    if (typeof wishlist.likesCount === 'number' && !isNaN(wishlist.likesCount)) {
      return wishlist.likesCount;
    }
    
    // Fallback to likes array length
    if (Array.isArray(wishlist.likes)) {
      return wishlist.likes.length;
    }
    
    // Final fallback
    return 0;
  }

  /**
   * Check if current user has liked the wishlist
   * @param {Object} wishlist - The wishlist object
   * @param {string} userId - Current user ID
   * @returns {boolean} - True if user has liked the wishlist
   */
  static isLikedByUser(wishlist, userId) {
    if (!wishlist || !userId) return false;
    
    // Check the likes array
    if (Array.isArray(wishlist.likes)) {
      return wishlist.likes.includes(userId);
    }
    
    // Check the likedByMe property if it exists
    if (typeof wishlist.likedByMe === 'boolean') {
      return wishlist.likedByMe;
    }
    
    return false;
  }

  /**
   * Toggle like for a wishlist with atomic database update
   * @param {string} wishlistId - The wishlist ID
   * @param {string} userId - Current user ID
   * @param {Object} currentWishlist - Current wishlist state
   * @returns {Promise<Object>} - Updated wishlist data
   */
  static async toggleLike(wishlistId, userId, currentWishlist) {
    if (!wishlistId || !userId || !currentWishlist) {
      throw new Error('Missing required parameters for toggleLike');
    }
    
    try {
      const wishlistRef = doc(db, "wishlists", wishlistId);
      
      let updatedWishlist = null;
      
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(wishlistRef);
        if (!snap.exists()) {
          throw new Error(`Wishlist ${wishlistId} does not exist`);
        }

        const data = snap.data();
        const currentLikes = Array.isArray(data.likes) ? data.likes : [];
        
        // CRITICAL FIX: Use transaction snapshot data, not client state to avoid race conditions
        const alreadyLiked = currentLikes.includes(userId);
        
        // Create new likes array, ensuring no duplicates with Set deduplication
        let newLikes;
        if (alreadyLiked) {
          newLikes = currentLikes.filter(id => id !== userId);
        } else {
          // Use Set to prevent duplicates in case of concurrent updates
          newLikes = [...new Set([...currentLikes, userId])];
        }

        const newLikesCount = newLikes.length;

        transaction.update(wishlistRef, {
          likes: newLikes,
          likesCount: newLikesCount
        });

        // Prepare the updated wishlist object for return
        updatedWishlist = {
          ...currentWishlist,
          likes: newLikes,
          likesCount: newLikesCount,
          likedByMe: !alreadyLiked
        };
      });

      console.log(`✅ Like ${updatedWishlist.likedByMe ? 'added' : 'removed'} for wishlist ${wishlistId}`);
      return updatedWishlist;
      
    } catch (error) {
      console.error('❌ Error toggling like:', error);
      throw error;
    }
  }

  /**
   * Update local state after like toggle
   * @param {Array} wishlists - Current wishlists array
   * @param {string} wishlistId - The wishlist ID that was liked/unliked
   * @param {Object} updatedWishlist - The updated wishlist data from toggleLike
   * @returns {Array} - Updated wishlists array
   */
  static updateWishlistsAfterLike(wishlists, wishlistId, updatedWishlist) {
    return wishlists.map(w => 
      w.id === wishlistId ? updatedWishlist : w
    );
  }

  /**
   * Sort wishlists by popularity (likes count)
   * @param {Array} wishlists - Array of wishlists to sort
   * @param {boolean} ascending - Sort order (default: false for descending)
   * @returns {Array} - Sorted wishlists array
   */
  static sortByPopularity(wishlists, ascending = false) {
    if (!Array.isArray(wishlists)) return [];
    
    return [...wishlists].sort((a, b) => {
      const aLikes = this.getLikesCount(a);
      const bLikes = this.getLikesCount(b);
      
      return ascending ? aLikes - bLikes : bLikes - aLikes;
    });
  }

  /**
   * Sort wishlists by creation date
   * @param {Array} wishlists - Array of wishlists to sort
   * @param {boolean} ascending - Sort order (default: false for most recent first)
   * @returns {Array} - Sorted wishlists array
   */
  static sortByDate(wishlists, ascending = false) {
    if (!Array.isArray(wishlists)) return [];
    
    return [...wishlists].sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      
      return ascending ? aTime - bTime : bTime - aTime;
    });
  }

  /**
   * Calculate total likes for a user's wishlists
   * @param {Array} wishlists - Array of user's wishlists
   * @returns {number} - Total likes count
   */
  static getTotalLikesForUser(wishlists) {
    if (!Array.isArray(wishlists)) return 0;
    
    return wishlists.reduce((total, wishlist) => {
      return total + this.getLikesCount(wishlist);
    }, 0);
  }

  /**
   * Normalize wishlist data to ensure consistent structure
   * @param {Object} wishlist - Raw wishlist data
   * @param {string} userId - Current user ID
   * @returns {Object} - Normalized wishlist object
   */
  static normalizeWishlist(wishlist, userId) {
    if (!wishlist) return null;

    const likes = Array.isArray(wishlist.likes) ? wishlist.likes : [];
    const likesCount = this.getLikesCount(wishlist);
    const likedByMe = userId ? this.isLikedByUser(wishlist, userId) : false;

    return {
      ...wishlist,
      likes,
      likesCount,
      likedByMe
    };
  }
}

// Export default for easier importing
export default LikesManager;